import { useAuth, readRefreshToken, type SessionUser } from '@/store/auth';

/**
 * Cycle de vie d'une session, côté navigateur.
 *
 * Trois choses vivent ici, et elles se tiennent :
 *
 *  1. LE RENOUVELLEMENT EST UNIQUE. Le serveur fait tourner le jeton de
 *     renouvellement à chaque échange : présenter deux fois le même est
 *     interprété — à juste titre — comme un rejeu, et ferme la session. Or
 *     l'ancien code lançait un renouvellement par requête tombée en 401, et
 *     chaque onglet menait sa vie. Il faut donc un verrou, et un verrou qui
 *     traverse les onglets : `navigator.locks` en est un vrai.
 *
 *  2. ON RENOUVELLE AVANT D'ÉCHOUER. Attendre le 401 marche, mais fait payer
 *     un aller-retour perdu à une requête sur deux quand le jeton est court —
 *     et il est court désormais.
 *
 *  3. L'INACTIVITÉ DÉCONNECTE. Le serveur ferme la session au bout du délai
 *     qu'il annonce ; ce minuteur-ci coupe à la même échéance, côté écran,
 *     pour que l'application ne reste pas ouverte sur des données après coup.
 *     Et il ne compte QUE l'activité humaine : une page qui rafraîchit ses
 *     données toute seule ne doit pas maintenir une session ouverte devant un
 *     bureau vide.
 */

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '';

/** Clé partagée entre onglets : dernière activité humaine, en millisecondes. */
const ACTIVITY_KEY = 'md.last_activity';

/** On ne réécrit pas l'activité à chaque mouvement de souris. */
const ACTIVITY_WRITE_THROTTLE_MS = 15_000;

/** Marge avant expiration du jeton d'accès. */
const REFRESH_SKEW_MS = 90_000;

/** Avertissement affiché ce temps avant la coupure. */
const WARN_BEFORE_MS = 60_000;

/** Défaut si le serveur n'annonce rien (ancienne version de l'API). */
const DEFAULT_IDLE_MS = 10 * 60 * 1000;

const TICK_MS = 5_000;

type LoginBody = {
  accessToken: string;
  refreshToken: string;
  accountId: number;
  email: string;
  displayName: string;
  source: string;
  roles: string[];
  expiresAt: string;
  sessionId?: number;
  idleTimeoutSec?: number;
  sessionExpiresAt?: string;
};

export function toSessionUser(body: LoginBody): SessionUser {
  return {
    accountId: body.accountId,
    email: body.email,
    displayName: body.displayName,
    source: body.source,
    roles: body.roles,
    expiresAt: body.expiresAt,
  };
}

/** Installe la session rendue par /login ou /refresh. */
export function adoptSession(body: LoginBody) {
  useAuth.getState().setSession(body.accessToken, body.refreshToken, toSessionUser(body), {
    idleTimeoutSec: body.idleTimeoutSec,
    sessionExpiresAt: body.sessionExpiresAt,
  });
}

// ---------------------------------------------------------------------------
// Renouvellement — un seul à la fois, y compris entre onglets
// ---------------------------------------------------------------------------

let inflight: Promise<string | null> | null = null;

/**
 * Renouvelle le jeton d'accès et retourne le nouveau, ou null si la session est
 * finie.
 *
 * Le verrou est pris AVANT de lire le jeton de renouvellement, pas après :
 * c'est tout l'intérêt. Un onglet qui attend son tour relit la valeur écrite
 * par celui qui vient de passer, et présente donc un jeton valide plutôt que
 * celui, déjà consommé, qu'il avait en main en entrant.
 */
export function refreshSession(): Promise<string | null> {
  if (inflight) return inflight;
  inflight = withCrossTabLock('md.session.refresh', doRefresh).finally(() => {
    inflight = null;
  });
  return inflight;
}

async function doRefresh(): Promise<string | null> {
  const refreshToken = readRefreshToken();
  if (!refreshToken) {
    endSession('expired');
    return null;
  }
  let res: Response;
  try {
    res = await fetch(`${BASE_URL}/v1/auth/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });
  } catch {
    // Réseau coupé : on ne déconnecte pas. Le jeton est peut-être encore bon,
    // et jeter la session parce que le wifi a hoqueté serait pénible.
    return null;
  }
  if (!res.ok) {
    endSession('expired');
    return null;
  }
  const body = (await res.json()) as LoginBody;
  adoptSession(body);
  return body.accessToken;
}

/**
 * S'assure qu'un jeton utilisable est en main. Appelé avant les requêtes qui ne
 * passent pas par le client typé — un téléversement de 30 Mo n'a pas droit à
 * une seconde chance.
 */
export async function ensureFreshToken(): Promise<string | null> {
  const { accessToken, user } = useAuth.getState();
  if (!accessToken) return refreshSession();
  const expiresAt = user?.expiresAt ? Date.parse(user.expiresAt) : NaN;
  if (Number.isNaN(expiresAt) || expiresAt - Date.now() > REFRESH_SKEW_MS) {
    return accessToken;
  }
  return (await refreshSession()) ?? accessToken;
}

/**
 * Verrou partagé entre onglets. `navigator.locks` existe partout où cette
 * application tourne ; le repli sert les contextes où il manque (vieux
 * navigateur, test), auquel cas on retombe sur l'unicité par onglet.
 */
function withCrossTabLock<T>(name: string, fn: () => Promise<T>): Promise<T> {
  const locks = (navigator as Navigator & { locks?: LockManager }).locks;
  if (!locks) return fn();
  return locks.request(name, fn) as Promise<T>;
}

// ---------------------------------------------------------------------------
// Inactivité
// ---------------------------------------------------------------------------

let lastActivity = Date.now();
let lastActivityWrite = 0;
let watching = false;
let timer: ReturnType<typeof setInterval> | null = null;

function idleLimitMs(): number {
  const sec = useAuth.getState().idleTimeoutSec;
  return sec && sec > 0 ? sec * 1000 : DEFAULT_IDLE_MS;
}

/**
 * Activité humaine. Partagée entre onglets par localStorage : travailler dans
 * un onglet ne doit pas laisser l'autre se déconnecter, ils sont la même
 * session.
 */
export function noteActivity() {
  lastActivity = Date.now();
  if (lastActivity - lastActivityWrite < ACTIVITY_WRITE_THROTTLE_MS) return;
  lastActivityWrite = lastActivity;
  try {
    localStorage.setItem(ACTIVITY_KEY, String(lastActivity));
  } catch {
    /* stockage refusé : l'inactivité reste alors propre à cet onglet */
  }
}

function lastActivityAcrossTabs(): number {
  try {
    const raw = Number(localStorage.getItem(ACTIVITY_KEY));
    if (Number.isFinite(raw) && raw > lastActivity) return raw;
  } catch {
    /* ignoré */
  }
  return lastActivity;
}

const ACTIVITY_EVENTS = ['pointerdown', 'keydown', 'wheel', 'touchstart'] as const;

/**
 * Démarre la surveillance : activité, renouvellement anticipé, coupure.
 * Idempotent — appelé au montage de l'application.
 */
export function startSessionWatch() {
  if (watching) return;
  watching = true;

  for (const ev of ACTIVITY_EVENTS) {
    window.addEventListener(ev, noteActivity, { passive: true });
  }
  // Revenir sur l'onglet est une activité en soi ; et c'est le moment où l'on
  // veut vérifier qu'on n'a pas dépassé le délai pendant l'absence.
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      noteActivity();
      void tick();
    }
  });
  window.addEventListener('storage', onStorage);

  noteActivity();
  timer = setInterval(() => void tick(), TICK_MS);
}

export function stopSessionWatch() {
  if (timer) clearInterval(timer);
  timer = null;
  watching = false;
}

async function tick() {
  const { accessToken, refreshToken } = useAuth.getState();
  if (!accessToken && !refreshToken) return;

  const idleFor = Date.now() - lastActivityAcrossTabs();
  const limit = idleLimitMs();

  if (idleFor >= limit) {
    endSession('idle');
    return;
  }

  const remaining = limit - idleFor;
  useAuth.getState().setIdleWarning(remaining <= WARN_BEFORE_MS ? Math.ceil(remaining / 1000) : null);

  // Renouvellement anticipé — et seulement si quelqu'un est là. Renouveler une
  // page laissée ouverte reviendrait à repousser indéfiniment la coupure que
  // l'on vient d'installer.
  if (idleFor > limit / 2) return;
  const expiresAt = useAuth.getState().user?.expiresAt;
  const ms = expiresAt ? Date.parse(expiresAt) - Date.now() : NaN;
  if (!Number.isNaN(ms) && ms < REFRESH_SKEW_MS) {
    await refreshSession();
  }
}

function onStorage(e: StorageEvent) {
  // Un autre onglet s'est déconnecté : on suit. Sans ça, celui-ci resterait
  // affiché avec ses données à l'écran.
  if (e.key === 'md.refresh_token' && e.newValue === null) {
    useAuth.getState().clear();
  }
}

// ---------------------------------------------------------------------------
// Fin de session
// ---------------------------------------------------------------------------

export type EndReason = 'user' | 'idle' | 'expired';

let ending = false;

/**
 * Ferme la session : côté serveur d'abord — c'est ce qui manquait, une
 * déconnexion ne faisait qu'oublier le jeton dans le navigateur pendant qu'il
 * restait valide ailleurs — puis localement.
 *
 * L'appel serveur est « au mieux » : hors ligne, on efface quand même. Un
 * bouton « quitter » qui ne quitte pas parce que le réseau est tombé serait
 * pire que tout.
 */
export function endSession(reason: EndReason = 'user') {
  if (ending) return;
  ending = true;

  const { accessToken } = useAuth.getState();
  const refreshToken = readRefreshToken();

  useAuth.getState().clear();

  if (refreshToken || accessToken) {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
    // keepalive : la requête survit à la navigation qui suit.
    void fetch(`${BASE_URL}/v1/auth/logout`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ refreshToken }),
      keepalive: true,
    }).catch(() => undefined);
  }

  useAuth.getState().setEndReason(reason);
  ending = false;
}
